import { applyArchive, readArchive } from '@vp/core';
import { request } from './catalog';
import { logger, storage, storageHealth } from './platform';
import { useApp } from './stores/app';

/**
 * The welcome canvas (uploaded in the admin panel): on the first start with no canvases the app
 * imports it and opens it, so a new user sees an example instead of an empty "My canvases". Once per
 * browser: deleting it does not bring it back, and people who already have canvases never get it.
 * Without a server, offline or switched off — nothing happens.
 */
const SEEN = 'welcome.seen';

export async function maybeWelcome(): Promise<void> {
  if (storageHealth.state === 'memory') return;
  try {
    if ((await storage.getSetting(SEEN)) !== undefined) return;
    // not a new user: remember it, so a later empty list (everything deleted) does not count as a first start
    if ((await storage.listCanvases()).length) {
      await storage.setSetting(SEEN, 'existing');
      return;
    }
    const { res, base } = await request('/api/v1/welcome');
    if (!res.ok) return;
    const info = (await res.json()) as { enabled?: boolean; version?: number };
    if (!info.enabled) return;
    const file = await fetch(base + '/api/v1/welcome/archive', { credentials: 'omit', referrerPolicy: 'no-referrer' });
    if (!file.ok) return;
    const parsed = await readArchive(new Uint8Array(await file.arrayBuffer()));
    // the user made a canvas meanwhile: theirs comes first, no example on top of it
    if ((await storage.listCanvases()).length) {
      await storage.setSetting(SEEN, 'existing');
      return;
    }
    const ids = await applyArchive(storage, parsed);
    await storage.setSetting(SEEN, info.version ?? 1);
    logger.info('welcome.imported', { canvases: ids.length });
    const app = useApp();
    await app.refreshCanvases();
    if (ids[0] && app.view === 'home') await app.openCanvas(ids[0]);
  } catch (e) {
    // offline, no server, a damaged archive: the user just starts with an empty list (asked again next start)
    logger.warn('welcome.failed', { error: e instanceof Error ? e.name : 'unknown' });
  }
}
